/**
 * App-wide "setup changed" notifier.
 *
 * Routy changes company settings, hours, booking forms and the team while the
 * pages showing them may already be open, and those pages load once and hold.
 * Writers call `notifySetupChanged(area)` after the server write succeeds; the
 * pages listen and reload.
 *
 * On the web this is a window event. Mobile has its own module at this same
 * path backed by an in-memory emitter, so the Routy logic synced between the
 * two platforms can call `notifySetupChanged()` without knowing which one it
 * is running on.
 */
export type SetupArea =
  | 'company_settings'
  | 'company_hours'
  | 'locations'
  | 'booking_forms'
  | 'team'
  | 'availability';

export const SETUP_CHANGED_EVENT = 'routemize:setup-changed';

export function notifySetupChanged(area: SetupArea): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent<SetupArea>(SETUP_CHANGED_EVENT, { detail: area }));
}

/** Calls `listener` with the changed area; returns the unsubscribe. */
export function subscribeSetupChanges(listener: (area: SetupArea) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (event: Event) => listener((event as CustomEvent<SetupArea>).detail);
  window.addEventListener(SETUP_CHANGED_EVENT, handler);
  return () => window.removeEventListener(SETUP_CHANGED_EVENT, handler);
}
